import type {
  CockpitColumnDef,
  CockpitColumnOption,
  CockpitPricingContext,
  CockpitRow,
} from "../types";
import * as XLSX from "xlsx";

export type CockpitPricingContextField = {
  label: string;
  value: string;
  tooltip?: string;
};

type CockpitCell = {
  type?: string;
  value?: unknown;
  options?: unknown[];
};

const asCell = (value: unknown): CockpitCell | null =>
  value && typeof value === "object" ? (value as CockpitCell) : null;

const cellValue = (value: unknown) => {
  const cell = asCell(value);
  return cell ? String(cell.value ?? "") : value != null ? String(value) : "";
};

const normaliseCockpitOptions = (options: unknown): CockpitColumnOption[] => {
  if (!Array.isArray(options)) return [];

  return options
    .map((option) => {
      if (option && typeof option === "object") {
        const item = option as Record<string, unknown>;
        const key = String(item.key ?? item.id ?? "");
        return {
          key,
          value: String(item.value ?? item.label ?? key),
        };
      }

      return {
        key: String(option),
        value: String(option),
      };
    })
    .filter((option) => option.key);
};

export const mapCockpitSelectValue = (
  options: CockpitColumnOption[] = [],
  input: string,
  mode: "key" | "label" = "key",
) => {
  const normalized = String(input).trim();
  const match = options.find(
    (option) =>
      option.key === normalized ||
      option.value === normalized ||
      option.key.toLowerCase() === normalized.toLowerCase() ||
      option.value.toLowerCase() === normalized.toLowerCase(),
  );
  if (!match) return normalized;
  return mode === "key" ? match.key : match.value;
};

export const normaliseCockpitRowData = (
  rows: CockpitRow[],
  columns: CockpitColumnDef[],
): CockpitRow[] =>
  rows.map((row) => {
    const next = { ...row };
    columns.forEach((column) => {
      if (column.type.toLowerCase() !== "select" || !next[column.key]) return;
      next[column.key] = mapCockpitSelectValue(
        column.values,
        next[column.key],
        "key",
      );
    });
    return next;
  });

export const getCockpitOptionLabel = (
  options: CockpitColumnOption[] = [],
  storedValue: string,
) => mapCockpitSelectValue(options, storedValue, "label");

export const normaliseCockpitColumns = (columns: unknown): CockpitColumnDef[] => {
  if (!Array.isArray(columns)) return [];

  return columns
    .filter(
      (column): column is Record<string, unknown> =>
        !!column && typeof column === "object",
    )
    .map((column) => ({
      key: String(column.key ?? ""),
      label: String(column.label ?? column.key ?? ""),
      type: String(column.type ?? "text"),
      visible: column.visible !== false,
      disabled: column.disabled === true,
      required: column.required === true,
      values: normaliseCockpitOptions(column.options),
    }))
    .filter((column) => column.key && column.visible);
};

export const normaliseCockpitRow = (
  row: Record<string, unknown>,
  index: number,
): CockpitRow => {
  const id = String(row.id ?? `cockpit-row-${index}`);
  const normalised: CockpitRow = { id };

  Object.entries(row).forEach(([key, value]) => {
    if (key !== "id") {
      normalised[key] = cellValue(value);
    }
  });

  return normalised;
};

export const normaliseCockpitRows = (
  rows: unknown[] = [],
  columns: CockpitColumnDef[] = [],
): CockpitRow[] =>
  normaliseCockpitRowData(
    rows.map((row, index) =>
      normaliseCockpitRow((row || {}) as Record<string, unknown>, index),
    ),
    columns,
  );

export const parseCockpitPayload = (payload: unknown) => {
  const source =
    payload && typeof payload === "object" && !Array.isArray(payload)
      ? (payload as Record<string, unknown>)
      : {};

  const rawRows = Array.isArray(payload)
    ? payload
    : (source.rows as unknown[]) ?? [];
  const columns = normaliseCockpitColumns(source.columnDef ?? source.columns);
  const rows = normaliseCockpitRows(rawRows, columns);
  const pricingContext = Array.isArray(payload)
    ? null
    : ((source.pricingContext ?? source.pricing_context ?? null) as
        | CockpitPricingContext
        | null);

  return {
    rows,
    columns,
    pricingContext,
    hasData: columns.length > 0,
  };
};

export const cloneCockpitRows = (rows: CockpitRow[]): CockpitRow[] =>
  rows.map((row) => ({ ...row }));

const getEditableColumns = (columns: CockpitColumnDef[]) =>
  columns.filter((column) => !column.disabled);

const getRequiredColumns = (columns: CockpitColumnDef[]) =>
  columns.filter((column) => !column.disabled && column.required);

export const isCockpitCellEmpty = (value: unknown) =>
  String(value ?? "").trim() === "";

export const rowHasAnyValue = (
  row: CockpitRow,
  columns: CockpitColumnDef[],
): boolean =>
  getEditableColumns(columns).some(
    (column) => !isCockpitCellEmpty(row[column.key]),
  );

export const rowHasAllRequired = (
  row: CockpitRow,
  columns: CockpitColumnDef[],
): boolean => {
  const requiredColumns = getRequiredColumns(columns);
  if (requiredColumns.length === 0) {
    return false;
  }

  return requiredColumns.every(
    (column) => !isCockpitCellEmpty(row[column.key]),
  );
};

const rowsEqual = (
  left: CockpitRow,
  right: CockpitRow,
  columns: CockpitColumnDef[],
) =>
  getEditableColumns(columns).every(
    (column) =>
      String(left[column.key] ?? "") === String(right[column.key] ?? ""),
  );

export const hasCockpitChanges = (
  initialRows: CockpitRow[],
  currentRows: CockpitRow[],
  columns: CockpitColumnDef[],
): boolean => {
  const currentById = new Map(currentRows.map((row) => [row.id, row]));
  const initialById = new Map(initialRows.map((row) => [row.id, row]));

  if (initialRows.some((row) => !currentById.has(row.id))) {
    return true;
  }

  return currentRows.some((row) => {
    const original = initialById.get(row.id);
    if (!original) {
      return rowHasAnyValue(row, columns);
    }

    return !rowsEqual(row, original, columns);
  });
};

export const getChangedCockpitRows = (
  initialRows: CockpitRow[],
  currentRows: CockpitRow[],
  columns: CockpitColumnDef[],
): CockpitRow[] => {
  const initialById = new Map(initialRows.map((row) => [row.id, row]));

  return currentRows.filter((row) => {
    const original = initialById.get(row.id);
    if (!original) {
      return rowHasAnyValue(row, columns);
    }

    return !rowsEqual(row, original, columns);
  });
};

export const getCockpitValidationErrors = (
  rows: CockpitRow[],
  columns: CockpitColumnDef[],
): Set<string> => {
  const errors = new Set<string>();
  const requiredColumns = getRequiredColumns(columns);

  rows.forEach((row) => {
    if (!rowHasAnyValue(row, columns)) {
      return;
    }

    requiredColumns.forEach((column) => {
      if (isCockpitCellEmpty(row[column.key])) {
        errors.add(`${row.id}:${column.key}`);
      }
    });
  });

  return errors;
};

export const canApplyCockpitChanges = (
  initialRows: CockpitRow[],
  currentRows: CockpitRow[],
  columns: CockpitColumnDef[],
): boolean => {
  if (!hasCockpitChanges(initialRows, currentRows, columns)) {
    return false;
  }

  const requiredColumns = getRequiredColumns(columns);
  if (requiredColumns.length === 0) {
    return false;
  }

  const changedRows = getChangedCockpitRows(initialRows, currentRows, columns);
  return changedRows.every((row) => rowHasAllRequired(row, columns));
};

export const createEmptyCockpitRow = (
  columns: CockpitColumnDef[],
  index = 0,
): CockpitRow => {
  const row: CockpitRow = { id: `cockpit-row-${Date.now()}-${index}` };
  columns.forEach((column) => {
    row[column.key] = "";
  });
  return row;
};

export const buildDiscountCockpitPayload = (
  rows: CockpitRow[],
  columns: CockpitColumnDef[],
): Record<string, string>[] =>
  rows
    .filter((row) => rowHasAnyValue(row, columns))
    .map((row) =>
      columns.reduce<Record<string, string>>((entry, column) => {
        entry[column.key] = String(row[column.key] ?? "");
        return entry;
      }, {}),
    );

export const parseCockpitExcel = (
  file: File,
  columns: CockpitColumnDef[],
): Promise<CockpitRow[]> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const workbook = XLSX.read(event.target?.result, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const sheetRows = XLSX.utils.sheet_to_json<(string | number)[]>(sheet, {
        header: 1,
        defval: "",
      });
      const [headerRow = [], ...dataRows] = sheetRows;
      const labelToKey = Object.fromEntries(
        columns.map((column) => [column.label, column.key]),
      );
      const keyToKey = Object.fromEntries(
        columns.map((column) => [column.key, column.key]),
      );
      const keys = (headerRow as string[]).map((header) => {
        const trimmed = String(header).trim();
        return labelToKey[trimmed] ?? keyToKey[trimmed] ?? "";
      });
      const rows = dataRows
        .filter((row) => row.some((cell) => String(cell ?? "").trim() !== ""))
        .map((row, index) => {
          const cockpitRow = createEmptyCockpitRow(columns, index);
          keys.forEach((key, colIndex) => {
            if (!key) return;
            const column = columns.find((item) => item.key === key);
            let cellValue = String(row[colIndex] ?? "").trim();
            if (column?.type.toLowerCase() === "select") {
              cellValue = mapCockpitSelectValue(column.values, cellValue, "key");
            }
            cockpitRow[key] = cellValue;
          });
          return cockpitRow;
        });

      if (rows.length === 0) {
        reject(
          new Error(
            "No data found in the uploaded file. Please add at least one data row.",
          ),
        );
        return;
      }

      const requiredColumns = getRequiredColumns(columns);
      const invalidRow = rows.findIndex(
        (row) =>
          !requiredColumns.every(
            (column) => !isCockpitCellEmpty(row[column.key]),
          ),
      );

      if (invalidRow >= 0) {
        const row = rows[invalidRow];
        const missingLabels = requiredColumns
          .filter((column) => isCockpitCellEmpty(row[column.key]))
          .map((column) => column.label);
        reject(
          new Error(
            `Row ${invalidRow + 1} is missing required values: ${missingLabels.join(", ")}`,
          ),
        );
        return;
      }

      resolve(normaliseCockpitRowData(rows, columns));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });

export const downloadCockpitTemplate = (
  columns: CockpitColumnDef[],
  rows: CockpitRow[] = [],
) => {
  const keys = columns.map((column) => column.key);
  const sheetData = [
    columns.map((column) => column.label),
    ...rows.map((row) =>
      keys.map((key) => {
        const column = columns.find((item) => item.key === key);
        const cellValue = row[key] ?? "";
        if (column?.type.toLowerCase() === "select") {
          return getCockpitOptionLabel(column.values, cellValue);
        }
        return cellValue;
      }),
    ),
  ];
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    workbook,
    XLSX.utils.aoa_to_sheet(sheetData),
    "Cockpit",
  );
  XLSX.writeFile(workbook, `cockpit-template-${Date.now()}.xlsx`);
};

export const buildCockpitPricingContext = (
  pricingContext: CockpitPricingContext | null | undefined,
  currency?: string,
  subscriptionDurationMonths?: number,
): { description: string; fields: CockpitPricingContextField[] } => {
  const durationMonths = subscriptionDurationMonths ?? 0;
  const description =
    pricingContext?.description ||
    `Pricing manager allocates the CAD, Reserve for Cost and Reserve for Subscription duration - ${durationMonths} months`;

  return {
    description,
    fields: [
      {
        label: "Currency",
        value: pricingContext?.currency || currency || "—",
        tooltip: "Currency",
      },
      {
        label: "CAD",
        value: pricingContext?.cad || "Discount",
        tooltip: "CAD",
      },
      {
        label: "Sales Reserve",
        value: pricingContext?.salesReserve || "Reserve for Price/Sales",
        tooltip: "Sales Reserve",
      },
      {
        label: "Cost Reserve",
        value: pricingContext?.costReserve || "Reserve for Cost",
        tooltip: "Cost Reserve",
      },
      {
        label: "MLP Adjustment",
        value: pricingContext?.mlpAdjustment || "Inflation adjustment",
        tooltip: "MLP Adjustment",
      },
    ],
  };
};
